import { fetchWithChatFeedback } from './chatFeedbackRequired';
/**
 * `historical` means this workflow has a valid earlier handoff, even if a
 * newer revision has not itself been handed off.
 */
export type MarketResearchHandoffState = 'active' | 'historical' | 'not_handed_off' | 'date_unavailable';

/** A current or historic authorized receipt makes human collaboration available. */
export const isMarketResearchHandoffAvailable = (state: MarketResearchHandoffState): boolean => (
  state === 'active' || state === 'historical'
);

export interface MarketResearchHandoffSummary {
  state: MarketResearchHandoffState;
  handed_off_at: string | null;
}

export interface MarketResearchHandoffMessage {
  message_id: string;
  sequence: number;
  sender_email: string;
  body: string;
  sent_at: string;
}

export interface MarketResearchHandoffConversation {
  version: string;
  handoff: MarketResearchHandoffSummary;
  messages: MarketResearchHandoffMessage[];
  earlier_cursor: string | null;
}

const genericError = 'We could not load the handoff conversation. Please try again.';
const sendError = 'We could not send that message. Please try again.';
const apiUrl = (endpoint: string): string => `${process.env.REACT_APP_API_URL || ''}${endpoint}`;
const emailShape = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const uuidShape = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const canonicalUtcShape = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.\d{1,6})?Z$/;

const isRecord = (value: unknown): value is Record<string, unknown> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

const isCanonicalUtc = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  const match = canonicalUtcShape.exec(value);
  if (!match) return false;
  const parsed = Date.parse(`${match[1]}Z`);
  return Number.isFinite(parsed) && new Date(parsed).toISOString().slice(0, 19) === match[1];
};

const parseConversation = (value: unknown): MarketResearchHandoffConversation => {
  if (!isRecord(value)
    || (value.version !== 1 && value.version !== '1')
    || !isRecord(value.handoff)
    || !['active', 'historical', 'not_handed_off', 'date_unavailable'].includes(value.handoff.state as string)
    || !(value.handoff.handed_off_at === null || isCanonicalUtc(value.handoff.handed_off_at))
    || !Array.isArray(value.messages)
    || !(value.earlier_cursor === undefined || value.earlier_cursor === null || typeof value.earlier_cursor === 'string')) {
    throw new Error(genericError);
  }
  const messages = value.messages.map((message): MarketResearchHandoffMessage => {
    if (!isRecord(message)
      || Object.keys(message).some((key) => !['message_id', 'sequence', 'sender_email', 'body', 'sent_at'].includes(key))
      || typeof message.message_id !== 'string' || !message.message_id
      || typeof message.sequence !== 'number' || !Number.isSafeInteger(message.sequence) || message.sequence < 1
      || typeof message.sender_email !== 'string' || !emailShape.test(message.sender_email)
      || typeof message.body !== 'string' || !message.body || message.body.length > 4000
      || !isCanonicalUtc(message.sent_at)) {
      throw new Error(genericError);
    }
    return {
      message_id: message.message_id,
      sequence: message.sequence,
      sender_email: message.sender_email,
      body: message.body,
      sent_at: message.sent_at,
    };
  });
  if (messages.some((message, index) => index > 0 && message.sequence <= messages[index - 1].sequence)) {
    throw new Error(genericError);
  }
  return {
    version: String(value.version),
    handoff: { state: value.handoff.state as MarketResearchHandoffState, handed_off_at: value.handoff.handed_off_at as string | null },
    messages,
    earlier_cursor: (value.earlier_cursor as string | null | undefined) ?? null,
  };
};

export async function getMarketResearchHandoffAvailability(
  flowId: string,
): Promise<{ handoff: Pick<MarketResearchHandoffSummary, 'state'> }> {
  let response: Response;
  try {
    response = await fetchWithChatFeedback(apiUrl(
      `/api/market-research/flows/${encodeURIComponent(flowId)}/handoff-availability`,
    ), { method: 'GET', credentials: 'include', headers: { Accept: 'application/json' } });
  } catch {
    throw new Error(genericError);
  }
  if (response.status === 401) window.dispatchEvent(new Event('auth-expired'));
  if (!response.ok) throw new Error(genericError);
  const value = await response.json().catch(() => null);
  if (!isRecord(value) || Object.keys(value).length !== 1
    || !isRecord(value.handoff) || Object.keys(value.handoff).length !== 1
    || !['active', 'historical', 'not_handed_off', 'date_unavailable'].includes(value.handoff.state as string)) {
    throw new Error(genericError);
  }
  return { handoff: { state: value.handoff.state as MarketResearchHandoffState } };
}

export async function getMarketResearchHandoffConversation(
  flowId: string,
  before?: string | null,
): Promise<MarketResearchHandoffConversation> {
  const params = new URLSearchParams({ limit: '50' });
  if (before) params.set('before', before);
  let response: Response;
  try {
    response = await fetchWithChatFeedback(apiUrl(
      `/api/market-research/flows/${encodeURIComponent(flowId)}/handoff-conversation?${params.toString()}`,
    ), { method: 'GET', credentials: 'include', headers: { Accept: 'application/json' } });
  } catch {
    throw new Error(genericError);
  }
  if (response.status === 401) window.dispatchEvent(new Event('auth-expired'));
  if (!response.ok) throw new Error(genericError);
  return parseConversation(await response.json().catch(() => null));
}

export async function sendMarketResearchHandoffMessage(
  flowId: string,
  body: string,
  clientMessageId: string,
): Promise<MarketResearchHandoffMessage> {
  if (!uuidShape.test(clientMessageId)) throw new Error(sendError);
  const normalized = body.replace(/\r\n?/g, '\n');
  let response: Response;
  try {
    response = await fetchWithChatFeedback(apiUrl(
      `/api/market-research/flows/${encodeURIComponent(flowId)}/handoff-conversation`,
    ), {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ body: normalized, client_message_id: clientMessageId }),
    });
  } catch {
    throw new Error(sendError);
  }
  if (response.status === 401) window.dispatchEvent(new Event('auth-expired'));
  if (!response.ok) throw new Error(sendError);
  const result = await response.json().catch(() => null);
  if (!isRecord(result)) throw new Error(sendError);
  const page = parseConversation({
    version: 1,
    handoff: { state: 'active', handed_off_at: null },
    messages: [result.message],
    earlier_cursor: null,
  });
  return page.messages[0];
}
